import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Handshake, RefreshCw, CheckCircle2, XCircle, Clock, Mail, Phone, Copy, ChevronLeft, FileText, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import { DocumentDropzone } from '../components/DocumentDropzone';
import type { Promoter } from '../types/promoter';

type FilterTab = 'pending' | 'active' | 'rejected';

const LEGAL_LABELS: Record<string, string> = {
  auto: 'Auto-entrepreneur', company: 'Société', individual: 'Particulier',
};

const PAGE_SIZE = 10;

export default function AdminPromotersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [promoters, setPromoters] = useState<Promoter[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<FilterTab>('pending');
  const [page, setPage] = useState(1);
  const [actioning, setActioning] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    try {
      // Tri simple sur 1 champ (pas d'index composite — convention Festin).
      const snap = await getDocs(query(collection(db, 'promoters'), orderBy('createdAt', 'desc')));
      setPromoters(snap.docs.map(d => ({ ...(d.data() as Promoter), uid: d.id })));
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des promoteurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [tab]);

  const handleAction = async (promoterUid: string, action: 'validate' | 'reject') => {
    if (action === 'reject' && !rejectReason.trim()) {
      toast.error('Indiquez un motif de refus');
      return;
    }
    setActioning(promoterUid + action);
    try {
      const token = await user!.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoters/${promoterUid}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(action === 'reject' ? { rejectionReason: rejectReason } : {}),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      if (action === 'validate') toast.success(`Validé — code ${j.affiliateCode}`);
      else toast.success('Candidature rejetée');
      setRejectReason('');
      await load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setActioning(null);
    }
  };

  // Fallback : active manuellement le code (contrat signé hors YouSign).
  const handleActivateManual = async (promoterUid: string) => {
    setActioning(promoterUid + 'activate');
    try {
      const token = await user!.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoters/${promoterUid}/activate-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ signedContractUrl: signedUrls[promoterUid] || '' }),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      toast.success('Code activé — le promoteur est notifié');
      await load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setActioning(null);
    }
  };

  // Filtrage JS (pas de query multi-champs — convention Festin).
  const filtered = promoters.filter(p => (p.status || 'pending') === tab);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const counts = {
    pending: promoters.filter(p => (p.status || 'pending') === 'pending').length,
    active: promoters.filter(p => p.status === 'active').length,
    rejected: promoters.filter(p => p.status === 'rejected').length,
  };

  const fmtDate = (d: any) => {
    if (!d) return '—';
    const date = d.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 pb-32 text-white">
      <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-gray-500 hover:text-white text-sm font-medium mb-6 transition-colors">
        <ChevronLeft className="w-4 h-4" /> Control Center
      </button>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3">
            <Handshake className="w-8 h-8 text-[#FF5C00]" />
            Promoteurs
          </h1>
          <p className="text-gray-400 mt-1">Validation des candidatures apporteurs d'affaires.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 text-sm font-medium">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
        </button>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {([
          { id: 'pending', label: 'En attente', icon: <Clock className="w-3.5 h-3.5" />, count: counts.pending },
          { id: 'active', label: 'Validés', icon: <CheckCircle2 className="w-3.5 h-3.5" />, count: counts.active },
          { id: 'rejected', label: 'Rejetés', icon: <XCircle className="w-3.5 h-3.5" />, count: counts.rejected },
        ] as const).map(f => (
          <button
            key={f.id}
            onClick={() => setTab(f.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
              tab === f.id ? 'bg-[#FF5C00] text-white' : 'bg-white/5 text-gray-400 hover:text-white border border-white/5'
            }`}
          >
            {f.icon}{f.label}
            <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${tab === f.id ? 'bg-white/20' : 'bg-white/10'}`}>{f.count}</span>
          </button>
        ))}
      </div>

      {/* Motif de refus (partagé) */}
      {tab === 'pending' && pageItems.length > 0 && (
        <input
          type="text"
          placeholder="Motif de refus (requis pour rejeter)"
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-white/20 mb-4"
        />
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-[#FF5C00]" /></div>
      ) : pageItems.length === 0 ? (
        <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-12 text-center">
          <p className="text-gray-500">Aucun promoteur dans cette catégorie.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pageItems.map(p => (
            <div key={p.uid} className="bg-zinc-900/50 border border-white/10 rounded-2xl p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-black text-white">{p.firstName} {p.lastName}</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-gray-400">
                      {LEGAL_LABELS[p.legalStatus] || p.legalStatus}
                    </span>
                    {p.affiliateCode && (
                      <button
                        onClick={() => { navigator.clipboard.writeText(p.affiliateCode!); toast.success('Code copié'); }}
                        className="inline-flex items-center gap-1 text-[10px] font-black text-[#FF5C00] bg-[#FF5C00]/10 border border-[#FF5C00]/20 px-2 py-0.5 rounded-full hover:bg-[#FF5C00]/20"
                      >
                        {p.affiliateCode} <Copy className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 flex-wrap">
                    <a href={`mailto:${p.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail className="w-3 h-3" />{p.email}</a>
                    <a href={`tel:${p.phone}`} className="flex items-center gap-1.5 hover:text-white"><Phone className="w-3 h-3" />{p.phone}</a>
                    <span className="text-gray-600">Candidature : {fmtDate(p.createdAt)}</span>
                  </div>
                  {p.context && (
                    <p className="text-xs text-gray-500 mt-2 bg-white/3 border border-white/5 rounded-lg px-3 py-2 italic">"{p.context}"</p>
                  )}
                </div>

                {/* Actions — uniquement en attente */}
                {(p.status || 'pending') === 'pending' && (
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => handleAction(p.uid, 'validate')}
                      disabled={actioning !== null}
                      className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-sm rounded-xl transition-all"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {actioning === p.uid + 'validate' ? '…' : 'Valider'}
                    </button>
                    <button
                      onClick={() => handleAction(p.uid, 'reject')}
                      disabled={actioning !== null}
                      className="flex items-center gap-2 px-4 py-2.5 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 text-red-400 font-bold text-sm rounded-xl border border-red-500/20 transition-all"
                    >
                      <XCircle className="w-4 h-4" />
                      {actioning === p.uid + 'reject' ? '…' : 'Rejeter'}
                    </button>
                  </div>
                )}
                {p.status === 'active' && (
                  <span className="shrink-0 inline-flex items-center gap-1.5 text-emerald-400 text-xs font-black px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {p.codeActive ? 'Code actif' : 'Validé — contrat en attente'}
                  </span>
                )}
                {p.status === 'rejected' && (
                  <span className="shrink-0 inline-flex items-center gap-1.5 text-red-400 text-xs font-black px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-full">
                    <XCircle className="w-3.5 h-3.5" /> Rejeté
                  </span>
                )}
              </div>

              {/* Contrat + activation — validé mais pas encore signé */}
              {p.status === 'active' && !p.codeActive && (
                <div className="mt-4 pt-4 border-t border-white/8 space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.contractUrl && (
                      <a href={p.contractUrl} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-400 bg-blue-500/10 border border-blue-500/20 px-3 py-1.5 rounded-lg hover:bg-blue-500/20">
                        <FileText className="w-3.5 h-3.5" /> Contrat généré
                      </a>
                    )}
                    <span className="text-xs text-gray-500">
                      {p.yousignSignatureId
                        ? 'Signature YouSign en attente — activation auto à la signature.'
                        : 'Le promoteur peut signer en ligne (case + nom) → activation auto. Sinon, activez ici manuellement.'}
                    </span>
                  </div>

                  {/* Fallback manuel : upload du contrat signé + activation */}
                  <div className="bg-white/3 border border-white/5 rounded-xl p-3">
                    <p className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">Activation manuelle (fallback)</p>
                    <DocumentDropzone
                      label="Contrat signé (optionnel)"
                      storagePath={`promoters/${p.uid}/contract_signed.pdf`}
                      onUploadComplete={(url) => setSignedUrls(prev => ({ ...prev, [p.uid]: url }))}
                      existingUrl={signedUrls[p.uid] || null}
                      accept="application/pdf"
                    />
                    <button
                      onClick={() => handleActivateManual(p.uid)}
                      disabled={actioning !== null}
                      className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-[#FF5C00] hover:bg-[#e05000] disabled:opacity-50 text-white font-bold text-sm rounded-xl transition-all"
                    >
                      <KeyRound className="w-4 h-4" />
                      {actioning === p.uid + 'activate' ? 'Activation…' : 'Activer le code'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm disabled:opacity-30 hover:bg-white/10"
          >Précédent</button>
          <span className="text-sm text-gray-400 px-3">Page {page} / {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm disabled:opacity-30 hover:bg-white/10"
          >Suivant</button>
        </div>
      )}
    </div>
  );
}
